import { ProductFlowMapping, ProductInfo, AddonOption } from '../types/orderFlow';

/**
 * 商品データ管理とマッピング処理を担当するユーティリティクラス
 */
export class ProductDataManager {
  private static instance: ProductDataManager;
  private mappings: ProductFlowMapping[] = [];
  private products: Record<string, ProductInfo> = {};
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): ProductDataManager {
    if (!ProductDataManager.instance) {
      ProductDataManager.instance = new ProductDataManager();
    }
    return ProductDataManager.instance;
  }

  /**
   * window.wakoCustomFlowDataからデータを初期化
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // window オブジェクトからデータを取得
      const config = window.wakoCustomFlowData;
      if (!config) {
        throw new Error('Product flow configuration data not found');
      }

      // Liquidから渡されるデータ構造に合わせて変換
      this.mappings = this.transformMappingData(config.mappings || []);
      this.products = this.extractProductsFromMappings(this.mappings);
      this.isInitialized = true;
    } catch (error) {
      throw error;
    }
  }

  /**
   * 利用可能な商品マッピングを取得
   */
  public getAvailableProducts(): ProductFlowMapping[] {
    return this.mappings;
  }

  /**
   * 指定された条件に合致する商品を検索
   */
  public findProduct(criteria: {
    productType?: string;
    beanType?: string;
    packageType?: string;
  }): ProductFlowMapping | null {
    return this.mappings.find(mapping => {
      const matches = [
        !criteria.productType || mapping.productType === criteria.productType,
        !criteria.beanType || mapping.beanType === criteria.beanType,
        !criteria.packageType || mapping.packageType === criteria.packageType
      ];
      return matches.every(Boolean);
    }) || null;
  }

  /**
   * 商品ハンドルから商品情報を取得
   */
  public getProductInfo(handle: string): ProductInfo | null {
    return this.products[handle] || null;
  }

  /**
   * 指定されたロット数とバリアントIDで価格を取得
   */
  public getVariantPrice(productHandle: string, lotSize: string): number {
    const product = this.getProductInfo(productHandle);
    if (!product) return 0;

    const variant = product.variants.find(v => v.lotSize === lotSize);
    return variant ? parseFloat(variant.price) : 0;
  }

  /**
   * 商品タグから特定のキーの値を抽出
   */
  public getTagValue(tags: string[], key: string): string | null {
    const tag = tags.find(tag => tag.startsWith(`${key}:`));
    return tag ? tag.split(':')[1] : null;
  }

  /**
   * オプション価格を計算
   */
  public calculateOptionPrice(
    optionSetDefinition: any,
    optionType: string,
    lotSize: string
  ): number {
    try {
      const options = optionSetDefinition[optionType];
      if (!options || !Array.isArray(options)) return 0;

      const option = options.find((opt: AddonOption) => 
        opt.title && opt.title.includes(lotSize)
      );

      return option ? parseFloat(option.price) : 0;
    } catch (error) {
      return 0;
    }
  }

  /**
   * 現在の状態が有効かチェック
   */
  public getIsInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * デバッグ用データ出力
   */
  public getDebugInfo(): object {
    return {
      isInitialized: this.isInitialized,
      mappingsCount: this.mappings.length,
      productsCount: Object.keys(this.products).length,
      mappings: this.mappings.map(m => ({
        targetProductHandle: m.targetProductHandle,
        productType: m.productType,
        beanType: m.beanType,
        packageType: m.packageType,
        displayName: m.displayName
      }))
    };
  }

  /**
   * フィルター条件に基づいて銘柄を取得
   */
  public getAvailableBeanVarieties(productType: string): string[] {
    const varieties = new Set<string>();
    
    this.mappings
      .filter(mapping => mapping.productType === productType && mapping.beanType === 'wako')
      .forEach(mapping => {
        const product = this.getProductInfo(mapping.targetProductHandle);
        if (product) {
          const beanTypeTag = this.getTagValue(product.tags, 'flow_bean_type');
          if (beanTypeTag && beanTypeTag.startsWith('wako-')) {
            varieties.add(beanTypeTag);
          }
        }
      });

    return Array.from(varieties);
  }

  /**
   * Liquidから渡されるデータをProductFlowMapping形式に変換
   */
  private transformMappingData(rawMappings: any[]): ProductFlowMapping[] {
    return rawMappings.map((mapping) => {
      
      const transformed = {
        targetProductHandle: mapping.targetProductHandle,
        productType: mapping.productType,
        beanType: mapping.beanType,
        packageType: mapping.packageType,
        productInfo: {
          id: mapping.mainProduct.handle,
          handle: mapping.mainProduct.handle,
          title: mapping.mainProduct.title,
          description: '',
          tags: [], // タグ情報は別途必要に応じて追加
          variants: mapping.mainProduct.variants.map((v: any) => ({
            id: v.gid,
            title: v.title,
            price: v.price,
            sku: v.sku || '',
            available: v.available,
            lotSize: v.option1 || v.title // ロット数はoption1またはtitleから推定
          })),
          images: []
        },
        optionSetReference: {
          setIdentifier: mapping.optionSet.set_identifier,
          displayName: mapping.optionSet.set_identifier,
          ...mapping.optionSet.options // オプション情報をそのまま展開
        },
        displayName: `${mapping.productType}-${mapping.beanType}-${mapping.packageType}`
      };
      
      return transformed;
    });
  }

  /**
   * マッピングデータから商品情報を抽出
   */
  private extractProductsFromMappings(mappings: ProductFlowMapping[]): Record<string, ProductInfo> {
    const products: Record<string, ProductInfo> = {};
    
    mappings.forEach(mapping => {
      products[mapping.targetProductHandle] = mapping.productInfo;
    });
    
    return products;
  }

  /**
   * リセット（テスト用）
   */
  public reset(): void {
    this.mappings = [];
    this.products = {};
    this.isInitialized = false;
  }
}

// シングルトンインスタンスをエクスポート
export const productDataManager = ProductDataManager.getInstance(); 